import { useState } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Container, Grid, Paper, TextField, Typography } from '@mui/material';
import { useAuth } from 'context/AuthContext';
import { useLogin, useDeleteAccount } from 'hooks/useUserAuth';
import { snackbarService } from 'services/snackbarService';
import useGlobalStyles from 'styles/globalStyles';
import PageHeader from 'components/common/PageHeader/PageHeader';
import CustomButton from 'components/buttons/CustomButton/CustomButton';
import CustomText from 'components/common/CustomText/CustomText';
import CustomDialog from 'components/dialogs/CustomDialog';

type LoginFormValues = {
  username: string;
  password: string;
};

const loginSchema = yup.object({
  username: yup
    .string()
    .trim()
    .email('Must use valid email address')
    .required('This field is required!'),
  password: yup.string().required('This field is required!'),
});

const AccountDeletionPage = () => {
  const globalStyles = useGlobalStyles();
  const { user, login, logout } = useAuth();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleted, setDeleted] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<LoginFormValues>({
    resolver: yupResolver(loginSchema),
  });

  const { mutate: loginMutation, isLoading: isLoggingIn } = useLogin(
    (res: any) => {
      const { data, status_code: statusCode } = res;
      if (statusCode === 200 || statusCode === 603) {
        login(data);
      } else {
        snackbarService.showSnackbar(data, 'error');
      }
    },
    () => {
      snackbarService.showSnackbar('Invalid email or password', 'error');
    }
  );

  const { mutate: deleteMutation, isLoading: isDeleting } = useDeleteAccount(
    async () => {
      setConfirmOpen(false);
      setDeleted(true);
      await logout();
      snackbarService.showSnackbar('Your account has been deleted.', 'success');
    },
    () => {
      snackbarService.showSnackbar('Failed to delete your account. Please try again.', 'error');
    }
  );

  const onSubmit = (values: LoginFormValues) => {
    loginMutation(values);
  };

  if (!user && !deleted) {
    return (
      <Box>
        <Grid
          className={globalStyles.boxSignIn}
          container
          direction="row"
          justifyContent="center"
          alignItems="center"
        >
          <Grid
            container
            size={{ xs: 12, sm: 12, md: 5 }}
            sx={{ justifyContent: 'center', alignItems: 'center' }}
            className={globalStyles.bigBox}
          >
            <Paper variant="outlined" className={globalStyles.paperSignIn}>
              <Box mb={3}>
                <Typography variant="subtitle1">Delete Your Account</Typography>
              </Box>
              <form onSubmit={handleSubmit(onSubmit)} className="container">
                <Typography
                  variant="body2"
                  className={globalStyles.forgetPasswordHeader}
                >
                  Log in to your MSLogger account below to request deletion of
                  your account and data.
                </Typography>
                <TextField
                  {...register('username')}
                  label="Email"
                  className="textField"
                  margin="normal"
                  helperText={errors.username?.message}
                  fullWidth
                  error={!!errors.username}
                  variant="standard"
                />
                <TextField
                  {...register('password')}
                  type="password"
                  label="Password"
                  className="textField"
                  margin="normal"
                  helperText={errors.password?.message}
                  fullWidth
                  error={!!errors.password}
                  variant="standard"
                />
                <CustomButton
                  fullWidth
                  text="Log In"
                  type="submit"
                  disabled={!isValid}
                  loading={isLoggingIn}
                />
              </form>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    );
  }

  return (
    <Box className={globalStyles.container}>
      <Container maxWidth="xl" className={globalStyles.container}>
        <Box pt={2}>
          <PageHeader
            title="Account Deletion"
            description={deleted ? '' : `Logged in as ${user.email}`}
          />
        </Box>

        <Box mt={3} p={3} className={globalStyles.sectionContainer}>
          {deleted ? (
            <CustomText text="Your MSLogger account and all associated data have been deleted." />
          ) : (
            <>
              <CustomText text="Deleting your account will permanently remove your MSLogger account and all associated data, including patient cases, registries, and settings. This action cannot be undone." />
              <Box mt={2}>
                <CustomButton
                  variant="filled"
                  color="error"
                  text="Delete My Account"
                  onClick={() => setConfirmOpen(true)}
                />
              </Box>
            </>
          )}
        </Box>
      </Container>

      <CustomDialog
        open={confirmOpen}
        title="Delete Account"
        message="Are you sure you want to permanently delete your account? This action cannot be undone."
        onClose={() => setConfirmOpen(false)}
        buttons={[
          <CustomButton
            key="cancel"
            variant="outlined"
            text="Cancel"
            onClick={() => setConfirmOpen(false)}
            height={35}
            disabled={isDeleting}
          />,
          <CustomButton
            key="yes"
            variant="filled"
            color="error"
            text="Yes, Delete"
            onClick={() => deleteMutation(user.id)}
            height={35}
            loading={isDeleting}
          />,
        ]}
      />
    </Box>
  );
};

export default AccountDeletionPage;
